// CHANGED: renamed file from pdfOrcamento.js to generateBugetPdf.js
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import Utils from "./utils";
import { logoFortioPDF } from "../assets/brand/logo-pdf";

const hasRobotoMedium = (vfs) => Boolean(vfs && vfs["Roboto-Medium.ttf"]);

const candidates = [
  pdfFonts,
  pdfFonts?.default,
  pdfFonts?.pdfMake,
  pdfFonts?.default?.pdfMake,
]
  .filter(Boolean)
  .map((item) => item.vfs || item.pdfMake?.vfs || item);

const resolvedVfs = candidates.find(hasRobotoMedium);

if (typeof pdfMake.addVirtualFileSystem === "function") {
  try {
    pdfMake.addVirtualFileSystem(pdfFonts);
  } catch (error) {
    // noop
  }
  if (pdfFonts?.default) {
    try {
      pdfMake.addVirtualFileSystem(pdfFonts.default);
    } catch (error) {
      // noop
    }
  }
}

if (resolvedVfs) {
  pdfMake.vfs = resolvedVfs;
}

pdfMake.fonts = {
  Roboto: {
    normal: "Roboto-Regular.ttf",
    bold: "Roboto-Medium.ttf",
    italics: "Roboto-Italic.ttf",
    bolditalics: "Roboto-MediumItalic.ttf",
  },
};

if (typeof pdfMake.addFonts === "function") {
  pdfMake.addFonts(pdfMake.fonts);
}

const formatCurrency = (value) =>
  Utils.toNumber(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const toPdfFileToken = (value, fallback) => {
  // Normaliza trecho do nome do arquivo para evitar caracteres inválidos.
  const token = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return token || fallback;
};

const normalizeItems = (itens, valorFrete) => {
  if (Array.isArray(itens) && itens.length > 0) {
    return itens.map((item, index) => {
      const qtd = Number(item?.qtd || item?.quantity || 1);
      const valor = Utils.toNumber(item?.valor || item?.value || item?.total);
      const total = Utils.toNumber(item?.total || valor * qtd);
      const descricao =
        item?.descricao ||
        item?.description ||
        item?.name ||
        item?.tipo ||
        `Item ${index + 1}`;

      return {
        item: index + 1,
        descricao: String(descricao),
        qtd: qtd || 1,
        valor,
        total,
      };
    });
  }

  const base = Utils.toNumber(valorFrete);
  return [
    {
      item: 1,
      descricao: "FRETE DEDICADO",
      qtd: 1,
      valor: base,
      total: base,
    },
  ];
};

export function gerarPdfOrcamento(dados, options = {}) {
  const {
    numero,
    dataOrcamento,
    pagador,
    vendedor,
    contatoNome,
    contatoTelefone,
    contatoEmail,
    vendedorTelefone,
    vendedorEmail,
    origemCidade,
    origemUF,
    destinoCidade,
    destinoUF,
    pontosAdicionais,
    previsao,
    modal,
    veiculo,
    carroceria,
    peso,
    valorTransportado,
    materiais,
    descricaoServicos,
    valorFrete,
    total,
    prazoPagamento,
    observacoes,
    itens,
  } = dados;

  const itensNormalizados = normalizeItems(itens, valorFrete);
  const totalItens = itensNormalizados.reduce((acc, item) => acc + Utils.toNumber(item.total), 0);
  const totalGeral = Utils.toNumber(total || totalItens);
  // Regra de paginação: somente acima de 6 itens, empurra a seção de observações/regras para a próxima página.
  const shouldBreakBeforeRules = itensNormalizados.length > 6;
  const observacaoLimpa = String(observacoes || "").trim();
  const observacoesTexto =
    observacaoLimpa &&
    observacaoLimpa !== "\\u2014" &&
    observacaoLimpa !== "--" &&
    observacaoLimpa !== "\u2014"
      ? observacaoLimpa
      : "LIVRE DE CARGA.\nLIVRE DE DESCARGA.";
  const adicionais = Number(pontosAdicionais || 0);
  const destinoComAdicionais = `${destinoCidade || "-"} - ${destinoUF || "-"}${adicionais > 0 ? `  + ${adicionais} (${adicionais === 1 ? "destino" : "destinos"})` : ""}` ;
  const vendedorContatoStack = [
    { text: vendedor || "-", style: "contactName", margin: [0, 8, 0, 0] },
    vendedorTelefone && vendedorTelefone !== "-" ? { text: vendedorTelefone, style: "contactLine" } : null,
    vendedorEmail && vendedorEmail !== "-" ? { text: vendedorEmail, style: "contactEmail" } : null,
  ].filter(Boolean);

  const itensBody = [
    [
      { text: "Item", style: "thCenter" },
      { text: "Descri\u00e7\u00e3o", style: "thLeft" },
      { text: "Qtd.", style: "thCenter" },
      { text: "Valor", style: "thRight" },
      { text: "Total", style: "thRight" },
    ],
    ...itensNormalizados.map((item) => [
      { text: String(item.item), style: "tdCenter" },
      { text: item.descricao, style: "tdLeft" },
      { text: item.qtd.toFixed(2), style: "tdCenter" },
      { text: formatCurrency(item.valor), style: "tdRight" },
      { text: formatCurrency(item.total), style: "tdRight" },
    ]),
    [
      { text: "VALOR TOTAL", colSpan: 4, style: "totalLabel" },
      {},
      {},
      {},
      { text: formatCurrency(totalGeral), style: "totalValue" },
    ],
  ];

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [28, 24, 28, 86],
    footer: () => ({
      margin: [28, 0, 28, 20],
      columns: [
        {
          width: "*",
          stack: [
            { text: "Fortio Transportes Colaborativos LTDA", style: "footerLeft" },
            { text: "Acesse nosso site: www.fortio.com.br", style: "footerLeft" },
          ],
        },
        {
          width: "auto",
          stack: [
            { text: "(31) 2342-0777", style: "footerRight" },
            { text: "contato@fortio.com.br", style: "footerRight" },
          ],
        },
      ],
    }),
    content: [
      {
        columns: [
          {
            width: "*",
            stack: [
              {
                columns: [
                  {
                    width: 125,
                    svg: `<svg viewBox=\"0 0 2528 533\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">
<image x=\"-1\" width=\"2529\" height=\"533\" preserveAspectRatio=\"none\" xlink:href=\"${logoFortioPDF}\"/>
</svg>`,
                    margin: [0, 1, 0, 0],
                  },
                ],
                columnGap: 8,
              },
              { text: "Fortio Transportes Colaborativos LTDA - 48.940.778/0001-07", style: "companyBold" },
              { text: "R. Bélgica, 38  - Gloria - Contagem / Minas Gerais - 32340-030", style: "company" },
              //{ text: "Contagem / Minas Gerais - 32340-030", style: "company" },
            ],
          },
          {
            width: "auto",
            stack: [
              { text: "Or\u00e7amento", style: "rightTitle" },
              { text: String(numero || "-"), style: "rightSubTitle" },
              { text: String(dataOrcamento || "-"), style: "rightDate" },
              // Sobe o bloco de contato da direita para reduzir espaço vazio no topo.
              ...vendedorContatoStack,
            ],
          },
        ],
        // Desce levemente o rodapé para melhorar a distribuição vertical.
        margin: [0, 10, 0, 0],
      },


     

      {
        text: [
          "A ",
          { text: pagador || "nome do pagador que esta no Or\u00e7amento", bold: true },
        ],
        // Sobe o início do conteúdo do orçamento para aproveitar melhor a área útil da página.
        margin: [0, 8, 0, 8],
        fontSize: 9,
      },
      {
        text: ["Aos Cuidados de: ", { text: contatoNome || "-", bold: true }],
        margin: [0, 0, 0, 8],
        fontSize: 9,
      },
      {
        text:
          "\u00c9 com prazer que apresentamos nossa proposta de servi\u00e7os conforme solicitado. Estamos comprometidos em fornecer solu\u00e7\u00f5es log\u00edsticas eficientes e confi\u00e1veis para atender \u00e0s suas necessidades de transporte de cargas.",
        margin: [0, 0, 0, 8],
        fontSize: 9,
      },
      {
        text: "Segue abaixo o detalhamento do or\u00e7amento e dos servi\u00e7os oferecidos:",
        margin: [0, 0, 0, 6],
        fontSize: 9,
      },

      {
        table: {
          widths: ["*", "*", "*", "*"],
          body: [
            [{ text: "Detalhes do Frete", colSpan: 4, style: "sectionHeader" }, {}, {}, {}],
            [
              { text: "Cidade de Origem:", style: "labelCell" },
              { text: `${origemCidade || "-"} - ${origemUF || "-"}`, style: "valueCell" },
              { text: "Cidade de Destino:", style: "labelCell" },
              { text: destinoComAdicionais, style: "valueCell" },
            ],
            [
              { text: "Modalidade:", style: "labelCell" },
              { text: modal || "-", style: "valueCell" },
              { text: "Prazo de Entrega:", style: "labelCell" },
              { text: previsao || "-", style: "valueCell" },
            ],
            [
              { text: "Valor Transportado:", style: "labelCell" },
              { text: valorTransportado || "-", style: "valueCell" },
              { text: "Peso (Kg):", style: "labelCell" },
              { text: peso || "-", style: "valueCell" },
            ],
            [
              { text: "Ve\u00edculo:", style: "labelCell" },
              { text: veiculo || "-", style: "valueCell" },
              { text: "Carroceria:", style: "labelCell" },
              { text: carroceria || "-", style: "valueCell" },
            ],
            [
              { text: "Desc. dos Materiais:", style: "labelCell" },
              { text: materiais || "-", colSpan: 3, style: "valueCell" },
              {},
              {},
            ],
            [
              { text: "Desc. dos Servi\u00e7os:", style: "labelCell" },
              { text: descricaoServicos || "FRETE DEDICADO.", colSpan: 3, style: "valueCell" },
              {},
              {},
            ],
          ],
        },
        layout: "lightHorizontalLines",
        margin: [0, 0, 0, 8],
      },

      {
        table: {
          widths: [40, "*", 42, 160, 160],
          body: itensBody,
        },
        layout: "lightHorizontalLines",
        margin: [0, 0, 0, 8],
      },

      {
        table: {
          widths: ["*", "auto", 220],
          body: [
            [
              { text: "Observa\u00e7\u00f5es", style: "sectionHeader", colSpan: 1 },
              { text: "Condi\u00e7\u00f5es de Pagamento:", style: "labelCell" },
              // Exibe no PDF a condição escolhida no resumo da negociação (insert-budget).
              { text: prazoPagamento || "-", style: "valueCell" },
            ],
            [
              { text: observacoesTexto, colSpan: 3, style: "valueCell", margin: [0, 6, 0, 6] },
              {},
              {},
            ],
          ],
        },
        layout: "lightHorizontalLines",
        // Quando a tabela de itens cresce, esta quebra evita esmagar as regras no fim da página.
        ...(shouldBreakBeforeRules ? { pageBreak: "before" } : {}),
      },
      {
        canvas: [
          { type: "line", x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 1, lineColor: "#000000" },
        ],
        // Sobe a linha divisória para otimizar o espaço vertical entre blocos.
        margin: [0, 10, 0, 12],
      },
      {
        text:
          "Agradecemos pela oportunidade de apresentar nossa proposta. Temos orgulho de oferecer uma solu\u00e7\u00e3o log\u00edstica abrangente e eficiente, com nossa vasta experi\u00eancia em fretes e nosso compromisso com a excel\u00eancia no atendimento. Acreditamos firmemente que nossa empresa \u00e9 a escolha ideal para atender \u00e0s suas necessidades de log\u00edstica, proporcionando a voc\u00ea tranquilidade, confiabilidade e resultados excepcionais. Esperamos ter a oportunidade de trabalhar em conjunto e demonstrar o valor que podemos agregar ao seu neg\u00f3cio.",
        style: "termsIntro",
      },
      {
        text: [
          "- ICMS acrescido nos pre\u00e7os conforme legisla\u00e7\u00e3o;\n",
          "- GRIS/ADVALOREM acrescido a CT-e 0,3% sobre o valor da mercadoria transportada (seguro contra roubo e avarias);\n",
          "- Seguradora: SOMPO - Ap\u00f3lices de RCTR e RCF-DC, com cobertura de at\u00e9 R$ 1.500.000,00 por caminh\u00e3o/viagem, para maiores valores pode ser solicitado embarque espor\u00e1dico (modalidade de seguro espec\u00edfico); para avarias apenas para produtos e mercadorias novos; limitado a R$ 200.000,00; demais condi\u00e7\u00f5es, consulte-nos;\n",
          "- Di\u00e1ria de nossos ve\u00edculos, s\u00e3o de R$550,00 para VUC, R$ 750,00 para Toco, R$ 950,00 para Trucks e de R$ 1.200,0 para carretas Simples (demais ve\u00edculos, consulte-nos);\n",
          "- ENTREGAS/ COLETAS DEMORADAS: O prazo m\u00e1ximo para atendimento em cargas e descargas \u00e9 de 5 horas. Ultrapassado o prazo, cobramos a TDE - Taxa de Dificuldade de Entrega, de R$ 3,29 por tonelada/hora ou fra\u00e7\u00e3o, j\u00e1 contabilizando as cinco horas iniciais\n",
          "- DEVOLU\u00c7\u00c3O - Custo de 100% do frete original;\n",
          "- RE-ENTREGA - Custo da Re-Entrega: 50% do valor do Frete cobrado;\n",
          "- CARGA/DESCARGA: Caso o servi\u00e7o n\u00e3o esteja descrito na proposta, havendo necessidade, o valor referente a este servi\u00e7o ser\u00e1 cobrado como adicional;\n",
          "- Conforme o artigo 754, par\u00e1grafo \u00fanico, do C\u00f3digo Civil, as avarias vis\u00edveis devem ser comunicadas imediatamente, enquanto as n\u00e3o vis\u00edveis devem ser denunciadas no prazo m\u00e1ximo de 10 dias a partir da data do recebimento.\n",
          "- N\u00e3o nos responsabilizamos pela falta de mercadorias ap\u00f3s assinatura dos canhotos e a libera\u00e7\u00e3o do motorista.\n",
          "- RETEN\u00c7\u00d5ES PELA FISCALIZA\u00c7\u00c3O, POR IRREGULARIDADE COM A MERCADORIA, OU COM OS DOCUMENTOS FISCAIS: \u00e9 de responsabilidade do contratante a solu\u00e7\u00e3o para o problema, bem como o ressarcimento \u00e0 empresa de todas as despesas relacionadas \u00e0 multas e di\u00e1rias por tempo parado dos ve\u00edculos.",
        ],
        style: "termsList",
        margin: [0, 10, 0, 18],
      },
    ],
    styles: {
      brandWord: { fontSize: 26, bold: true, color: "#062b52", margin: [0, -2, 0, 0] },
      companyBold: { fontSize: 9.5, bold: true, margin: [0, 1, 0, 0] },
      company: { fontSize: 8.8, margin: [0, 1, 0, 0] },
      rightTitle: { fontSize: 14, bold: true },
      rightSubTitle: { fontSize: 10, bold: true },
      rightDate: { fontSize: 9.2, margin: [0, 2, 0, 0] },
      contactName: { fontSize: 9.2, bold: true },
      contactLine: { fontSize: 8.8 },
      contactEmail: { fontSize: 8.8, color: "#1a56db", decoration: "underline" },
      sectionHeader: { fontSize: 8.8, bold: true, fillColor: "#f2f2f2" },
      labelCell: { fontSize: 8.2, bold: true },
      valueCell: { fontSize: 8.2 },
      thCenter: { fontSize: 9, bold: true, alignment: "center", fillColor: "#f2f2f2" },
      thLeft: { fontSize: 9, bold: true, alignment: "left", fillColor: "#f2f2f2" },
      thRight: { fontSize: 9, bold: true, alignment: "right", fillColor: "#f2f2f2" },
      tdLeft: { fontSize: 8.8 },
      tdCenter: { fontSize: 8.8, alignment: "center" },
      tdRight: { fontSize: 8.8, alignment: "right" },
      totalLabel: { fontSize: 10.5, bold: true },
      totalValue: { fontSize: 13, bold: true, alignment: "right" },
      termsIntro: { fontSize: 9, alignment: "justify" },
      termsList: { fontSize: 7 },
      // Ajuste solicitado: remove o espaco extra entre as duas linhas do rodape.
      footerLeft: { fontSize: 9, bold: true, margin: [0, 0, 0, 0] },
      footerRight: { fontSize: 9, bold: false, alignment: "right", margin: [0, 0, 0, 0] },
      footerMeta: { fontSize: 9, alignment: "right" },
    },
    defaultStyle: {
      fontSize: 9,
    },
  };

  const pdfGenerator = pdfMake.createPdf(docDefinition);

  if (options?.output === "base64") {
    return new Promise((resolve, reject) => {
      try {
        pdfGenerator.getBase64((base64) => resolve(base64));
      } catch (error) {
        reject(error);
      }
    });
  }

  // Fallback global de nome no padrão solicitado para qualquer chamada sem fileName explícito.
  const fallbackFileName = `Orcamento_${toPdfFileToken(pagador, "SEM_PAGADOR")}_${toPdfFileToken(origemUF, "SEM_ORIGEM")}_${toPdfFileToken(destinoUF, "SEM_DESTINO")}_${Date.now()}.pdf`;
  pdfGenerator.download(options?.fileName || fallbackFileName);
  return null;
}

















